/** Mounted memory globe context for mentioned frames and agent steering (spec §6). */
import { YOU } from "@/api/memory-focus";
import type { MentionedFrame } from "@/lib/frame-mentions";
import type { PocketLevel } from "@/stores/memory-pocket-stack";
import { useMemoryStore } from "@/stores/memory-store";

/** The globe's 60 drawn things plus 12 connection slots, and a bounded trail. */
export const MAX_LIT_IDS = 72;
export const MAX_BREADCRUMB = 20;

export interface MemoryFrameViewState {
  focus_id: string | null;
  lit_ids: string[];
  breadcrumb: string[];
  selected_id: string | null;
  pocket_level: PocketLevel | null;
}

/** Only the mounted globe knows which things are lit at full strength. */
export interface MemoryGlobeReading {
  litIds: readonly string[];
}

type ViewReader = () => MemoryGlobeReading;
const readers = new Map<string, ViewReader>();

export function registerMemoryFrameView(
  frameId: string,
  reader: ViewReader,
): () => void {
  readers.set(frameId, reader);
  return () => {
    if (readers.get(frameId) === reader) readers.delete(frameId);
  };
}

function memoryFrameViewState(frameId: string): MemoryFrameViewState | null {
  const reader = readers.get(frameId);
  if (!reader) return null;
  const { focusId, trail, youId, selection, pocketLevel } =
    useMemoryStore.getState();
  const thingId = (id: string) => (id === YOU ? youId : id);
  return {
    focus_id: thingId(focusId),
    lit_ids: reader().litIds.slice(0, MAX_LIT_IDS),
    breadcrumb: trail
      .slice(-MAX_BREADCRUMB)
      .map(thingId)
      .filter((id): id is string => id !== null),
    selected_id:
      selection?.type === "thing" || selection?.type === "fact"
        ? thingId(selection.id)
        : null,
    pocket_level: pocketLevel,
  };
}

/** Attach the live view only to mentioned memory frames mounted here. */
export function attachMemoryFrameViews(
  frames: MentionedFrame[] | undefined,
): MentionedFrame[] | undefined {
  return frames?.map((frame) => {
    const viewState =
      frame.frame_type === "memory"
        ? memoryFrameViewState(frame.frame_id)
        : null;
    return viewState ? { ...frame, view_state: viewState } : frame;
  });
}

/** The server validates focus and resolves open to an owned live thing or fact. */
export interface AgentMemoryView {
  focus?: string;
  open?:
    | { type: "thing"; id: string }
    | { type: "fact"; id: string; focus: string };
}

/** Open takes precedence because it also selects the thing or fact's focus. */
export function applyAgentMemoryView(
  store: typeof useMemoryStore,
  view: AgentMemoryView,
): void {
  if (view.focus) store.getState().setFocus(view.focus, "jump");
  if (view.open?.type === "thing")
    store.getState().openThing(view.open.id, undefined, "jump");
  if (view.open?.type === "fact") {
    store.getState().setFocus(view.open.focus, "jump");
    store.getState().select({ type: "fact", id: view.open.id });
  }
}
